/**
 * The signed-in user's role, as `@bono-packages/skills` wants it (D57): it decides who may install a
 * Skill and who only reads the catalogue and works in the Workspace. Null means read-only in a
 * customer app, so a role that never resolves silently makes every screen look broken.
 *
 * Read per call, not once: the plugin is installed before anybody has signed in.
 */
import { UserRoles } from "@/types/user";
import { useUserStore } from "@/stores/user";
import TokenService from "@/services/tokenService";

const ADMIN: string[] = [UserRoles.CARBON_ACCOUNTING_ADMIN, UserRoles.BONO_SUPER_ADMIN];

/** Before the user store is filled, the access token still carries the groups. */
function fromToken(): string | null {
  const token = TokenService.getLocalAccessToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    const groups: unknown = payload?.["cognito:groups"];
    if (!Array.isArray(groups)) return null;
    const admin = groups.find((g) => typeof g === "string" && ADMIN.includes(g));
    if (typeof admin === "string") return admin;
    return typeof groups[0] === "string" ? groups[0] : null;
  } catch {
    return null;
  }
}

export function skillsRole(): string | null {
  try {
    const roles = useUserStore().getUserRoles;
    const admin = roles.find((r) => ADMIN.includes(r.roleName));
    if (admin) return admin.roleName;
    if (roles.length) return roles[0].roleName;
  } catch {
    /* the store is not ready before the app mounts; the token still knows */
  }
  return fromToken();
}
